import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import zxcvbn from 'zxcvbn';
import './ActivationPage.css';
import { API_BASE } from '../config';
import { useTranslation } from '../translations';

const DEFAULT_TERMS_URL = 'https://the-rent.pl/regulamin.html';

// Te same progi co `modules/onboarding.js#assertPasswordStrength`: serwer odrzuca hasło krótsze
// niż 8 znaków, dłuższe niż 128 albo z oceną zxcvbn poniżej 3. Lista wymagań pokazuje WYŁĄCZNIE
// to, czego serwer naprawdę wymaga — wielka litera czy cyfra z ekranu Bezpieczeństwo nie są tu
// warunkiem, a dopisanie ich blokowałoby hasło, które serwer przyjmuje.
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 128;
const PASSWORD_MIN_SCORE = 3;

function strengthTone(score) {
  if (score >= PASSWORD_MIN_SCORE) return 'success';
  if (score === 2) return 'warning';
  return 'danger';
}

/**
 * Strona aktywacji organizacji
 * URL: /activate/:token
 */
function ActivationPage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  // R.3: adres regulaminu przychodzi z serwera (lib/terms.js), żeby wersja zapisana przy akceptacji
  // i link pokazany na stronie pochodziły z jednego miejsca. Stała niżej to tylko zapas.
  const [termsUrl, setTermsUrl] = useState(DEFAULT_TERMS_URL);
  const [isDemo, setIsDemo] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone_number: '',
    password: '',
    passwordConfirm: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [passwordFocused, setPasswordFocused] = useState(false);

  const passScore = useMemo(
    () => (formData.password ? zxcvbn(formData.password).score : 0),
    [formData.password]
  );
  const passLengthOk = formData.password.length >= PASSWORD_MIN_LENGTH;
  const passStrengthOk = passScore >= PASSWORD_MIN_SCORE;
  const passMatchOk = formData.passwordConfirm !== '' && formData.password === formData.passwordConfirm;
  const showPasswordPanel = passwordFocused || formData.password !== '';

  // Walidacja tokena przy załadowaniu strony
  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/activation/validate/${token}`);
      const data = await response.json();

      if (response.ok && data.valid) {
        setTokenValid(true);
        setOrganizationName(data.organizationName);
        if (data.termsUrl) setTermsUrl(data.termsUrl);
        setIsDemo(data.isDemo === true);
      } else {
        setError(data.error || t('activation.invalidToken'));
      }
    } catch (err) {
      setError(t('activation.connectionErrorLater'));
    } finally {
      setValidating(false);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Usuń błąd dla tego pola
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstname.trim()) {
      errors.firstname = t('activation.firstnameRequired');
    }

    if (!formData.lastname.trim()) {
      errors.lastname = t('activation.lastnameRequired');
    }

    if (!formData.email.trim()) {
      errors.email = t('activation.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('activation.emailInvalid');
    }

    if (!formData.phone_number.trim()) {
      errors.phone_number = t('activation.phoneRequired');
    } else if (!/^\d{9}$/.test(formData.phone_number.replace(/\s/g, ''))) {
      errors.phone_number = t('activation.phoneDigits');
    }

    if (!formData.password) {
      errors.password = t('activation.passwordRequired');
    } else if (formData.password.length < PASSWORD_MIN_LENGTH) {
      errors.password = t('activation.passwordMin8');
    } else if (formData.password.length > PASSWORD_MAX_LENGTH) {
      errors.password = t('activation.passwordMax128');
    } else if (!passStrengthOk) {
      errors.password = t('activation.passwordTooWeak');
    }

    if (formData.password !== formData.passwordConfirm) {
      errors.passwordConfirm = t('activation.passwordMismatch');
    }

    if (!acceptTerms) {
      errors.acceptTerms = t('activation.termsRequired');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/activation/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          firstname: formData.firstname,
          lastname: formData.lastname,
          email: formData.email,
          phone_number: formData.phone_number.replace(/\s/g, ''),
          password: formData.password,
          acceptTerms
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        // Przekierowanie do strony logowania po 3 sekundach
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.error || t('activation.activationError'));
      }
    } catch (err) {
      setError(t('activation.connectionErrorRetry'));
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="activation-page">
        <div className="activation-container">
          <div className="activation-spinner">
            <div className="spinner"></div>
            <p>{t('activation.checkingToken')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="activation-page">
        <div className="activation-container">
          <div className="activation-success">
            <div className="success-icon">✓</div>
            <h1>{t('activation.successTitle')}</h1>
            <p>{t('activation.successBefore')} <strong>{organizationName}</strong>{t('activation.successAfter')}</p>
            <p>{t('activation.redirecting')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="activation-page">
        <div className="activation-container">
          <div className="activation-error">
            <div className="error-icon">✕</div>
            <h1>{t('activation.invalidLinkTitle')}</h1>
            <p>{error}</p>
            <p>{t('activation.invalidLinkBody')}</p>
            <button onClick={() => navigate('/login')} className="btn-primary">
              {t('activation.goToLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="activation-page">
      <div className="activation-container">
        <div className="activation-header">
          <h1>the.rENT</h1>
          <h2>{t('activation.title')}</h2>
          <p className="organization-name">{t('activation.rentalLabel')} <strong>{organizationName}</strong></p>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="activation-form">
          <div className="form-section">
            <h3>{t('activation.adminSection')}</h3>
            <p className="form-description">
              {t('activation.adminSectionHint')}
            </p>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstname">{t('activation.firstname')}</label>
              <input
                type="text"
                id="firstname"
                name="firstname"
                value={formData.firstname}
                onChange={handleInputChange}
                className={formErrors.firstname ? 'error' : ''}
                placeholder={t('activation.phFirstname')}
                disabled={loading}
              />
              {formErrors.firstname && (
                <span className="field-error">{formErrors.firstname}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lastname">{t('activation.lastname')}</label>
              <input
                type="text"
                id="lastname"
                name="lastname"
                value={formData.lastname}
                onChange={handleInputChange}
                className={formErrors.lastname ? 'error' : ''}
                placeholder={t('activation.phLastname')}
                disabled={loading}
              />
              {formErrors.lastname && (
                <span className="field-error">{formErrors.lastname}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('activation.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={formErrors.email ? 'error' : ''}
              placeholder={t('activation.phEmail')}
              disabled={loading}
            />
            {formErrors.email && (
              <span className="field-error">{formErrors.email}</span>
            )}
            <span className="field-hint">{t('activation.emailHint')}</span>
          </div>

          <div className="form-group">
            <label htmlFor="phone_number">{t('activation.phone')}</label>
            <input
              type="tel"
              id="phone_number"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleInputChange}
              className={formErrors.phone_number ? 'error' : ''}
              placeholder="123456789"
              disabled={loading}
            />
            {formErrors.phone_number && (
              <span className="field-error">{formErrors.phone_number}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">{t('activation.password')}</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                onFocus={() => setPasswordFocused(true)}
                className={formErrors.password ? 'error' : ''}
                placeholder="••••••••"
                disabled={loading}
                autoComplete="new-password"
                aria-describedby="password-requirements"
              />
              {formErrors.password && (
                <span className="field-error">{formErrors.password}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="passwordConfirm">{t('activation.passwordConfirm')}</label>
              <input
                type="password"
                id="passwordConfirm"
                name="passwordConfirm"
                value={formData.passwordConfirm}
                onChange={handleInputChange}
                className={formErrors.passwordConfirm ? 'error' : ''}
                placeholder="••••••••"
                disabled={loading}
                autoComplete="new-password"
              />
              {formErrors.passwordConfirm && (
                <span className="field-error">{formErrors.passwordConfirm}</span>
              )}
            </div>
          </div>

          {/* Pod OBOMA polami, nie pod pierwszym: w układzie dwukolumnowym pasek i lista mieściłyby
              się w połowie szerokości, a „oba hasła takie same” dotyczy przecież obu pól. */}
          {showPasswordPanel && (
            <div className="password-panel" id="password-requirements" data-cy="activation-password-panel">
              {formData.password && (
                <div className={`password-strength tone-${strengthTone(passScore)}`} data-cy="activation-password-strength">
                  <div className="password-strength-label">
                    <span>{t('activation.passwordStrength')}</span>
                    <strong>{t(`passwordStrength.${passScore}`)}</strong>
                  </div>
                  <div
                    className="password-strength-bar"
                    role="meter"
                    aria-valuemin={0}
                    aria-valuemax={4}
                    aria-valuenow={passScore}
                    aria-valuetext={t(`passwordStrength.${passScore}`)}
                    aria-label={t('activation.passwordStrength')}
                  >
                    {/* Ocena 0 dostaje krótki odcinek, żeby pasek nie wyglądał na pusty. */}
                    <div className="password-strength-fill" style={{ width: `${Math.max(passScore, 0.4) * 25}%` }} />
                  </div>
                </div>
              )}
              <p className="password-panel-title">{t('activation.passwordRequirements')}</p>
              <ul className="password-requirements">
                <li className={passLengthOk ? 'ok' : ''} data-cy="activation-req-length">
                  <span className="req-mark" aria-hidden="true">{passLengthOk ? '✓' : '•'}</span>
                  {t('activation.reqLength', { min: PASSWORD_MIN_LENGTH })}
                </li>
                <li className={passStrengthOk ? 'ok' : ''} data-cy="activation-req-strength">
                  <span className="req-mark" aria-hidden="true">{passStrengthOk ? '✓' : '•'}</span>
                  {t('activation.reqStrength', { strength: t(`passwordStrength.${PASSWORD_MIN_SCORE}`) })}
                </li>
                <li className={passMatchOk ? 'ok' : ''} data-cy="activation-req-match">
                  <span className="req-mark" aria-hidden="true">{passMatchOk ? '✓' : '•'}</span>
                  {t('activation.reqMatch')}
                </li>
              </ul>
              <p className="password-tip">{t('activation.passwordTip')}</p>
            </div>
          )}

          <div className="form-group terms-group">
            <label className="terms-label" htmlFor="acceptTerms">
              <input
                type="checkbox"
                id="acceptTerms"
                name="acceptTerms"
                checked={acceptTerms}
                onChange={(e) => {
                  setAcceptTerms(e.target.checked);
                  if (formErrors.acceptTerms) setFormErrors(prev => ({ ...prev, acceptTerms: '' }));
                }}
                disabled={loading}
                aria-invalid={!!formErrors.acceptTerms || undefined}
                aria-describedby={formErrors.acceptTerms ? 'acceptTerms-error' : undefined}
                data-cy="activation-accept-terms"
              />
              <span>
                {t('activation.termsBefore')}{' '}
                <a href={termsUrl} target="_blank" rel="noopener noreferrer" data-cy="activation-terms-link">
                  {t('activation.termsLink')}
                </a>
                {t('activation.termsAfter')}
              </span>
            </label>
            {isDemo && (
              <span className="field-hint" data-cy="activation-demo-note">{t('activation.demoDataNote')}</span>
            )}
            {formErrors.acceptTerms && (
              <span className="field-error" id="acceptTerms-error">{formErrors.acceptTerms}</span>
            )}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary btn-large"
              disabled={loading}
            >
              {loading ? t('activation.submitting') : t('activation.submit')}
            </button>
          </div>

          <div className="form-footer">
            <p>
              {t('activation.footer')}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ActivationPage;
